export const ETAPES_SUIVI = ['COMMANDE_CONFIRMEE', 'PAIEMENT_ACCEPTE', 'COMMANDE_GLOBALE', 'PRODUITS_ARRIVES', 'MARQUAGE_PRODUCTION', 'PREPARATION', 'DISPONIBLE_RETRAIT', 'RETIRE_EXPEDIE'];

export const moyensPaiementCampagne = (campagne = {}) => {
  const configuration = campagne?.moyens_paiement
    || campagne?.communication_config?.moyens_paiement
    || {};
  return {
    carte_bancaire: configuration.carte_bancaire !== false,
    bon_club: configuration.bon_club !== false,
    payplug_live: configuration.payplug_live === true,
  };
};

export const definirMoyenPaiementCampagne = (communicationConfig = {}, cle, actif) => ({
  ...(communicationConfig || {}),
  moyens_paiement: {
    carte_bancaire: communicationConfig?.moyens_paiement?.carte_bancaire !== false,
    bon_club: communicationConfig?.moyens_paiement?.bon_club !== false,
    ...(communicationConfig?.moyens_paiement || {}),
    [cle]: !!actif,
  },
});

export const modesPaiementPanier = (panier = [], autorisations = {}) => {
  if (!panier.length) return [];
  const cb = autorisations.carte_bancaire !== false
    && panier.every((ligne) => (ligne.modes_autorises || ['CB']).includes('CB'));
  // L'éligibilité d'un Bon dépend du code et de sa cible, donc uniquement le
  // serveur peut la décider. Le checkout doit permettre sa saisie dès que la
  // campagne autorise les Bons, même si un ancien drapeau produit est absent.
  const bonMixte = autorisations.bon_club !== false;
  return [...(cb ? ['CB'] : []), ...(bonMixte ? ['BON_CLUB'] : [])];
};

export const nouveauxBons = () => [{ code: '', produit_id: '', validation: null }];

export const totalBonsCentimes = (bons = []) => bons.reduce((total, bon) => (
  total + (bon.validation?.valide ? Math.round(Number(bon.validation.valeur_ttc || 0) * 100) : 0)
), 0);

export const ventilationSimulation = (totalCentimes, bons = []) => {
  const total = Math.max(0, Math.round(Number(totalCentimes || 0)));
  const bonsValides = Math.min(total, totalBonsCentimes(bons));
  return { bonCentimes: bonsValides, cbCentimes: total - bonsValides };
};

export const empreintePanierSimulation = (panier = [], totalCentimes = 0) => JSON.stringify({
  total_centimes:Math.round(Number(totalCentimes || 0)),
  lignes:panier.map((ligne) => ({
    produit_id:String(ligne.produit_id || ''), quantite:Number(ligne.quantite || 0),
    montant_centimes:Math.round(Number(ligne.montant_centimes || 0)),
    articles:ligne.articles || [], options:ligne.options || [],
  })),
});

export const extraireCodeQrBon = (valeur = '') => {
  const brut = String(valeur || '').trim();
  if (!brut) return '';
  try {
    const url = new URL(brut);
    return String(url.searchParams.get('bon') || url.searchParams.get('code_bon') || '').trim().toUpperCase();
  } catch (_) {
    const match = brut.match(/(?:BON|BCC|BC)-[A-Z0-9-]{4,80}/i);
    return String(match?.[0] || '').toUpperCase();
  }
};

export const creerIdempotence = (cryptoApi) => {
  const generateur = cryptoApi || window.crypto || { getRandomValues: (bytes) => bytes.forEach((_, i) => { bytes[i] = Math.floor(Math.random() * 256); }) };
  const bytes = new Uint8Array(16);
  generateur.getRandomValues(bytes);
  return `web-${Array.from(bytes, (v) => v.toString(16).padStart(2, '0')).join('')}`;
};

export const verifierGroupePanier = (panier = [], ligne = {}) => {
  const groupe = String(ligne.groupe || '').trim().toUpperCase();
  const groupes = [...new Set(panier.map((item) => String(item.groupe || '').trim().toUpperCase()).filter(Boolean))];
  if (groupes.length && groupe && !groupes.includes(groupe)) return 'Les boutiques de groupes différents ne peuvent pas partager le même panier.';
  return '';
};

export const grouperPanierCampagnes = (panier = [], campagneDefaut = {}) => Object.values(panier.reduce((acc, ligne) => {
  const campagneId = ligne.campagne_id || campagneDefaut.id || 'campagne';
  if (!acc[campagneId]) acc[campagneId] = {
    id: campagneId,
    groupe: ligne.groupe || campagneDefaut.groupe,
    club_id: ligne.club_id || campagneDefaut.club_id,
    club_nom: ligne.club_nom || campagneDefaut.club_nom || 'Club',
    campagne_nom: ligne.campagne_nom || campagneDefaut.nom || 'Campagne',
    date_prevue: ligne.date_prevue || campagneDefaut.livraison_prevue,
    lieu_retrait: ligne.lieu_retrait || campagneDefaut.lieu_retrait_libre || '',
    retrait_autorise: ligne.retrait_autorise ?? campagneDefaut.retrait_autorise,
    expedition_autorisee: ligne.expedition_autorisee ?? campagneDefaut.expedition_autorisee,
    bareme_port: ligne.bareme_port || campagneDefaut.bareme_port || {},
    lignes: [],
  };
  acc[campagneId].lignes.push(ligne);
  return acc;
}, {}));

export const normaliserBaremePort = (valeur = {}) => {
  const paliers = valeur?.paliers && typeof valeur.paliers === 'object' ? valeur.paliers : {};
  return {
    paliers: Object.fromEntries(Object.entries(paliers).map(([quantite, montant]) => [String(Number(quantite)), Number(montant)])),
    supplement_a_partir_de: Number(valeur?.supplement_a_partir_de || 0),
    supplement_par_article: Number(valeur?.supplement_par_article || 0),
  };
};

export const quantitePhysiqueLigne = (ligne = {}) => {
  if (!ligne.pack) return Number(ligne.quantite || 0);
  return (ligne.articles || []).reduce((total, article) => total + Number(article.quantite || 0), 0);
};

export const calculerPort = (baremeBrut = {}, quantite = 0, livraison = 'RETRAIT') => {
  if (livraison !== 'DOMICILE' || quantite <= 0) return 0;
  const bareme = normaliserBaremePort(baremeBrut);
  const debut = bareme.supplement_a_partir_de;
  const exact = bareme.paliers[String(quantite)];
  if (Number.isFinite(exact) && exact >= 0) return exact;
  const base = bareme.paliers[String(debut - 1)];
  if (!Number.isSafeInteger(debut) || debut < 2 || !Number.isFinite(base) || base < 0 || !Number.isFinite(bareme.supplement_par_article) || bareme.supplement_par_article < 0) return null;
  if (quantite < debut) return null;
  return base + (quantite - debut + 1) * bareme.supplement_par_article;
};

export const calculerPortsParClub = (panier = [], livraison = 'RETRAIT') => Object.values(panier.reduce((clubs, ligne) => {
  const cle = ligne.club_id || 'club';
  if (!clubs[cle]) clubs[cle] = { club_id: cle, club_nom: ligne.club_nom || 'Club', quantite: 0, bareme: ligne.bareme_port || {} };
  clubs[cle].quantite += quantitePhysiqueLigne(ligne);
  return clubs;
}, {})).map((club) => ({ ...club, port_ttc: calculerPort(club.bareme, club.quantite, livraison) }));

export const totalCommande = (panier = [], livraison = 'RETRAIT', fraisPortTtc = 0) => {
  const sousTotalCentimes = panier.reduce((total, ligne) => total + Number(ligne.montant_centimes || 0), 0);
  const portCentimes = livraison === 'DOMICILE' ? Math.round(Number(fraisPortTtc || 0) * 100) : 0;
  return { sousTotalCentimes, portCentimes, totalCentimes: sousTotalCentimes + portCentimes };
};

export const validerCoordonnees = (client = {}, livraison = 'RETRAIT') => {
  if (!client.nom?.trim() || !client.prenom?.trim() || !client.email?.trim() || !client.telephone?.trim()) return 'Renseignez vos nom, prénom, email et téléphone.';
  if (!/^\S+@\S+\.\S+$/.test(client.email)) return 'Adresse email invalide.';
  if (livraison === 'DOMICILE' && (!client.adresse?.trim() || !client.code_postal?.trim() || !client.ville?.trim())) return 'Adresse, code postal et ville sont obligatoires pour la livraison.';
  return '';
};

export const referenceCommandeTest = (cryptoApi) => {
  const generateur = cryptoApi || window.crypto;
  const bytes = new Uint8Array(12);
  generateur.getRandomValues(bytes);
  return `BC-${new Date().getFullYear()}-${Array.from(bytes, (v) => v.toString(16).padStart(2, '0')).join('').toUpperCase()}`;
};

export const commandePublique = (commande = {}) => ({
  reference: commande.reference,
  statut_suivi: commande.statut_suivi,
  date_prevue: commande.date_prevue,
  lieu_retrait: commande.lieu_retrait,
  mode_livraison: commande.mode_livraison,
  moyen_paiement: commande.moyen_paiement,
  paiement_bon: Number(commande.paiement_bon || 0),
  paiement_cb: Number(commande.paiement_cb || 0),
  total_ttc: commande.total_ttc,
  chronologie: Array.isArray(commande.chronologie) ? commande.chronologie : [],
  campagnes: Array.isArray(commande.campagnes) ? commande.campagnes.map((item) => ({ reference:item.reference, date_prevue:item.date_prevue, lieu_retrait:item.lieu_retrait, mode_livraison:item.mode_livraison, statut_suivi:item.statut_suivi })) : [],
});

export const normaliserPersonnalisationsArticle = (article = {}) => {
  const quantite = Math.max(0, Math.trunc(Number(article.quantite || 0)));
  const unites = Array.isArray(article.personnalisations_unites) ? article.personnalisations_unites : [];
  if (!quantite || unites.length === quantite) return { ...article, personnalisations_unites:unites };
  const saisies = Array.isArray(article.personnalisations) ? article.personnalisations : [];
  const options = saisies.map((option) => ({
    option_id:String(option.option_id || option.id || '').trim(),
    valeur:String(option.valeur ?? option.texte ?? '').trim(),
  })).filter((option) => option.option_id && option.valeur);
  return {
    ...article,
    personnalisations_unites:Array.from({ length:quantite }, (_, index) => ({ unite:index + 1, options:options.map((option) => ({ ...option })) })),
  };
};

export const payloadCommande = ({ campagne, panier, client, livraison, paiement, codeBon, bons = [], beneficiaires = {}, acceptationCgv = null }) => ({
    campagne_id: campagne.id,
    client: { nom: client.nom.trim(), prenom: client.prenom.trim(), email: client.email.trim().toLowerCase(), telephone: client.telephone.trim() },
    livraison: { mode: livraison, adresse: livraison === 'DOMICILE' ? { adresse: client.adresse.trim(), complement: client.complement?.trim() || '', code_postal: client.code_postal.trim(), ville: client.ville.trim() } : null },
    paiement: {
      mode: paiement,
      // Compatibilité avec le moteur local historique (code_bon) tout en
      // conservant le contrat récent multi-Bons (codes_bons).
      code_bon: paiement === 'BON_CLUB' ? String(bons[0]?.code || codeBon || '').trim().toUpperCase() : null,
      codes_bons: paiement === 'BON_CLUB' && bons.length ? bons.map((bon) => ({ code:bon.code.trim().toUpperCase(), produit_id:bon.produit_id })) : null,
      simulation: false,
    },
    ...(acceptationCgv?.version ? { juridique:{ cgv_acceptee:acceptationCgv.acceptee === true, cgv_version:String(acceptationCgv.version) } } : {}),
    lignes: panier.map((ligne, index) => ({
      produit_id:ligne.produit_id,
      quantite:ligne.quantite,
      articles:(ligne.articles || []).map(normaliserPersonnalisationsArticle),
      options:ligne.options,
      beneficiaires:ligne.pack ? (beneficiaires[index] || []) : [],
    })),
  });
